import React, { useState, useCallback, useEffect } from "react";
import { Button, Radio, InputNumber, Input, message, Spin, Select, Tag, Card, Divider } from "antd";
import { ScissorOutlined, DownloadOutlined, RobotOutlined, CheckOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { PDFDocument } from "pdf-lib";
import { useIntl } from "react-intl";
import { saveAs } from "file-saver";
import { useDocumentProcessing } from "./DocumentProcessingContext";

interface PDFSplitterProps {
  pdfUrl: string | null;
  filename: string;
  collection?: string;
  onSuccess?: () => void | Promise<void>;
  aiOnly?: boolean;
}

interface SuggestedSplit {
  pages: number[];
  detectedKeywords: string[];
  suggestedType: string;
  title: string;
  confidence: "high" | "medium" | "low";
}

interface SplitPreviewResponse {
  filename: string;
  gstoreId: string;
  totalPages: number;
  suggestedSplits: SuggestedSplit[];
}

interface EditableSplit {
  pages: number[];
  pageInput: string;
  documentType: string;
  title: string;
  detectedKeywords: string[];
  confidence: string;
}

const pagesToInput = (pages: number[]): string => {
  if (pages.length === 0) return '';
  const sorted = [...pages].sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === prev + 1) {
      prev = sorted[i];
    } else {
      ranges.push(start === prev ? `${start}` : `${start}-${prev}`);
      start = sorted[i];
      prev = sorted[i];
    }
  }
  ranges.push(start === prev ? `${start}` : `${start}-${prev}`);
  return ranges.join(',');
};

const parsePageInput = (input: string, totalPages: number): number[] => {
  const pages = new Set<number>();
  const parts = input.split(',').map(s => s.trim()).filter(Boolean);
  for (const part of parts) {
    if (part.includes('-')) {
      const [startStr, endStr] = part.split('-').map(s => s.trim());
      const start = parseInt(startStr, 10);
      const end = parseInt(endStr, 10);
      if (!isNaN(start) && !isNaN(end)) {
        for (let i = start; i <= Math.min(end, totalPages); i++) {
          if (i >= 1) pages.add(i);
        }
      }
    } else {
      const page = parseInt(part, 10);
      if (!isNaN(page) && page >= 1 && page <= totalPages) pages.add(page);
    }
  }
  return Array.from(pages).sort((a, b) => a - b);
};

const DOCUMENT_TYPES = [
  "invoice",
  "assessment-report",
  "contract",
  "document",
  "quotation",
  "price-adjustment",
];

const confidenceColor: Record<string, string> = {
  high: "green",
  medium: "orange",
  low: "red",
};

const PDFSplitter: React.FC<PDFSplitterProps> = ({ pdfUrl, filename, collection, onSuccess, aiOnly = false }) => {
  const intl = useIntl();
  const { splitPreview, splitConfirm, closeModal, fetchDocuments, tabKey, getDocumentTypeBaseOnTabKey } = useDocumentProcessing();

  const [splitMethod, setSplitMethod] = useState<"pages" | "ranges">("pages");
  const [pageRanges, setPageRanges] = useState("");
  const [pagesPerFile, setPagesPerFile] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pdfBytes, setPdfBytes] = useState<ArrayBuffer | null>(null);
  const [totalPages, setTotalPages] = useState(0);

  // AI split state
  const [aiPreview, setAiPreview] = useState<SplitPreviewResponse | null>(null);
  const [editableSplits, setEditableSplits] = useState<EditableSplit[]>([]);
  const [isAiAnalyzing, setIsAiAnalyzing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const getDocumentTypeLabel = useCallback(
    (type: string) =>
      intl.formatMessage(
        { id: `document.type.${type}`, defaultMessage: type.replace(/-/g, " ") },
      ),
    [intl]
  );

  const documentTypeOptions = React.useMemo(() => {
    const backendTypes = editableSplits.map((s) => s.documentType).filter(Boolean);
    const types = Array.from(new Set([...DOCUMENT_TYPES, ...backendTypes]));
    return types.map((type) => ({
      value: type,
      label: getDocumentTypeLabel(type),
    }));
  }, [editableSplits, getDocumentTypeLabel]);

  const getFilename = useCallback(() => {
    if (filename && filename.trim()) {
      return filename;
    }
    if (pdfUrl) {
      try {
        const url = new URL(pdfUrl);
        const pathname = url.pathname;
        const urlFilename = pathname.split("/").pop() || "";
        if (urlFilename) return urlFilename;
      } catch {
        const match = pdfUrl.match(/([^/]+\.pdf)(?:\?|$)/i);
        if (match) return match[1];
      }
    }
    return "document";
  }, [filename, pdfUrl]);

  useEffect(() => {
    if (!pdfUrl) {
      setPdfBytes(null);
      setTotalPages(0);
      return;
    }

    let cancelled = false;
    const loadPdf = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(pdfUrl);
        if (!response.ok) throw new Error("Failed to fetch PDF");
        const bytes = await response.arrayBuffer();
        if (cancelled) return;

        const pdf = await PDFDocument.load(bytes);
        if (cancelled) return;

        setPdfBytes(bytes);
        setTotalPages(pdf.getPageCount());
      } catch {
        if (!cancelled) {
          message.error(intl.formatMessage({ id: "document.processing.split.loadError" }));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadPdf();
    return () => { cancelled = true; };
  }, [pdfUrl, intl]);

  const handleAiSplit = async () => {
    if (!filename) {
      message.error(intl.formatMessage({ id: "document.processing.split.ai.noFilename" }));
      return;
    }
    setIsAiAnalyzing(true);
    setAiPreview(null);
    setEditableSplits([]);
    try {
      const preview: SplitPreviewResponse = await splitPreview(filename, collection);
      setAiPreview(preview);
      setEditableSplits(
        preview.suggestedSplits.map((s) => ({
          pages: s.pages,
          pageInput: pagesToInput(s.pages),
          documentType: s.suggestedType,
          title: s.title,
          detectedKeywords: s.detectedKeywords,
          confidence: s.confidence,
        }))
      );
    } catch {
      message.error(intl.formatMessage({ id: "document.processing.split.ai.analyseError" }));
    } finally {
      setIsAiAnalyzing(false);
    }
  };

  const handleConfirmSplit = async () => {
    if (!aiPreview) return;

    const validParts = editableSplits.filter((s) => s.pages.length > 0);
    if (validParts.length === 0) {
      message.warning(intl.formatMessage({ id: "document.processing.split.ai.validation.noValidParts" }));
      return;
    }
    const missingType = editableSplits.some((s) => s.pages.length > 0 && !s.documentType);
    if (missingType) {
      message.warning(intl.formatMessage({ id: "document.processing.split.ai.validation.missingDocumentType" }));
      return;
    }

    setIsConfirming(true);
    try {
      await splitConfirm(
        aiPreview.gstoreId,
        editableSplits.map((s) => ({ pages: s.pages, documentType: s.documentType })),
        filename,
        collection
      );
      message.success(intl.formatMessage({ id: "document.processing.split.ai.confirmSuccess" }));
      setAiPreview(null);
      setEditableSplits([]);
      if (onSuccess) {
        await onSuccess();
      } else {
        await fetchDocuments(getDocumentTypeBaseOnTabKey(tabKey));
        closeModal();
      }
    } catch {
      message.error(intl.formatMessage({ id: "document.processing.split.ai.confirmError" }));
    } finally {
      setIsConfirming(false);
    }
  };

  const updateSplitType = (index: number, documentType: string) => {
    setEditableSplits((prev) =>
      prev.map((s, i) => (i === index ? { ...s, documentType } : s))
    );
  };

  const updateSplitPages = (index: number, pageInput: string) => {
    setEditableSplits((prev) =>
      prev.map((s, i) =>
        i === index ? { ...s, pageInput, pages: parsePageInput(pageInput, totalPages) } : s
      )
    );
  };

  const removeEditableSplit = (index: number) => {
    setEditableSplits((prev) => prev.filter((_, i) => i !== index));
  };

  const addEditableSplit = () => {
    setEditableSplits((prev) => [
      ...prev,
      { pages: [], pageInput: '', documentType: '', title: '', detectedKeywords: [], confidence: 'low' },
    ]);
  };

  const splitByPages = useCallback(async () => {
    if (!pdfBytes) return;

    setIsProcessing(true);
    try {
      const pdf = await PDFDocument.load(pdfBytes);
      const pageCount = pdf.getPageCount();
      const actualFilename = getFilename();
      const filesToDownload: { blob: Blob; fileName: string }[] = [];

      for (let i = 0; i < pageCount; i += pagesPerFile) {
        const newPdf = await PDFDocument.create();
        const endPage = Math.min(i + pagesPerFile, pageCount);

        const pageIndices = Array.from({ length: endPage - i }, (_, index) => i + index);
        const pages = await newPdf.copyPages(pdf, pageIndices);
        pages.forEach((page) => newPdf.addPage(page));

        const newPdfBytes = await newPdf.save();
        const blob = new Blob([newPdfBytes.buffer as ArrayBuffer], { type: "application/pdf" });
        const baseName = actualFilename.replace(/\.pdf$/i, "");
        filesToDownload.push({ blob, fileName: `${baseName}_${i + 1}-${endPage}.pdf` });
      }

      let filesCreated = 0;
      for (let i = 0; i < filesToDownload.length; i++) {
        const { blob, fileName } = filesToDownload[i];
        saveAs(blob, fileName);
        filesCreated++;
        if (i < filesToDownload.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
        }
      }

      if (filesCreated > 0) {
        message.success(
          intl.formatMessage({ id: "document.processing.split.successCount" }, { count: filesCreated })
        );
      }
    } catch {
      message.error(intl.formatMessage({ id: "document.processing.split.error" }));
    } finally {
      setIsProcessing(false);
    }
  }, [pdfBytes, pagesPerFile, getFilename, intl]);

  const splitByRanges = useCallback(async () => {
    if (!pdfBytes || !pageRanges.trim()) return;

    setIsProcessing(true);
    try {
      const pdf = await PDFDocument.load(pdfBytes);
      const pageCount = pdf.getPageCount();
      const actualFilename = getFilename();
      const ranges = pageRanges.split(",").map((r) => r.trim()).filter((r) => r);
      const filesToDownload: { blob: Blob; fileName: string }[] = [];

      for (const range of ranges) {
        try {
          const newPdf = await PDFDocument.create();
          let pageIndices: number[];

          if (range.includes("-")) {
            const parts = range.split("-").map((s) => s.trim());
            const startPage = parseInt(parts[0], 10);
            const endPage = parseInt(parts[1], 10);
            if (isNaN(startPage) || isNaN(endPage) || startPage < 1 || endPage < 1) continue;
            const start = Math.max(0, startPage - 1);
            const end = Math.min(pageCount - 1, endPage - 1);
            if (start > end) continue;
            pageIndices = Array.from({ length: end - start + 1 }, (_, index) => start + index);
          } else {
            const pageNumber = parseInt(range, 10);
            if (isNaN(pageNumber) || pageNumber < 1) continue;
            const pageIndex = pageNumber - 1;
            if (pageIndex >= 0 && pageIndex < pageCount) {
              pageIndices = [pageIndex];
            } else {
              continue;
            }
          }

          if (pageIndices.length === 0) continue;

          const pages = await newPdf.copyPages(pdf, pageIndices);
          pages.forEach((page) => newPdf.addPage(page));

          const newPdfBytes = await newPdf.save();
          const blob = new Blob([newPdfBytes.buffer as ArrayBuffer], { type: "application/pdf" });
          const baseName = actualFilename.replace(/\.pdf$/i, "");
          filesToDownload.push({ blob, fileName: `${baseName}_${range}.pdf` });
        } catch (rangeError) {
          console.error(`Error processing range ${range}:`, rangeError);
        }
      }

      let filesCreated = 0;
      for (let i = 0; i < filesToDownload.length; i++) {
        const { blob, fileName } = filesToDownload[i];
        try {
          saveAs(blob, fileName);
          filesCreated++;
        } catch (downloadError) {
          console.error(`Error downloading ${fileName}:`, downloadError);
        }
        if (i < filesToDownload.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
        }
      }

      if (filesCreated > 0) {
        message.success(
          intl.formatMessage({ id: "document.processing.split.successCount" }, { count: filesCreated })
        );
      }
    } catch {
      message.error(intl.formatMessage({ id: "document.processing.split.error" }));
    } finally {
      setIsProcessing(false);
    }
  }, [pdfBytes, pageRanges, getFilename, intl]);

  if (!pdfUrl) {
    return (
      <div style={{ textAlign: "center", padding: "40px 20px" }}>
        <ScissorOutlined style={{ fontSize: 48, color: "#d9d9d9" }} />
        <h3 style={{ marginTop: 16, color: "#8c8c8c" }}>
          {intl.formatMessage({ id: "document.processing.split.noFileSelected" })}
        </h3>
        <p style={{ color: "#bfbfbf" }}>
          {intl.formatMessage({ id: "document.processing.split.selectFile" })}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "40px 20px" }}>
        <Spin size="large" />
        <p style={{ marginTop: 16, color: "#8c8c8c" }}>
          {intl.formatMessage({ id: "document.processing.split.loading" })}
        </p>
      </div>
    );
  }


  return (
    <div style={{ padding: 16 }}>
      {/* AI Split Section */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <RobotOutlined style={{ fontSize: 18, color: "#722ed1" }} />
          <span style={{ fontWeight: 600, fontSize: 14 }}>{intl.formatMessage({ id: "document.processing.split.ai.sectionTitle" })}</span>
        </div>
        <Button
          icon={<RobotOutlined />}
          onClick={handleAiSplit}
          loading={isAiAnalyzing}
          disabled={!pdfBytes}
          style={{ borderColor: "#722ed1", color: "#722ed1" }}
        >
          {isAiAnalyzing
            ? intl.formatMessage({ id: "document.processing.split.ai.analysing" })
            : intl.formatMessage({ id: "document.processing.split.ai.button" })}
        </Button>

        {aiPreview && (
          <div style={{ marginTop: 16 }}>
            <p style={{ color: "#8c8c8c", marginBottom: 12, fontSize: 13 }}>
              {intl.formatMessage(
                { id: "document.processing.split.ai.detectedDocuments" },
                { count: editableSplits.length, pages: aiPreview.totalPages }
              )}
            </p>
            {editableSplits.map((split, index) => (
              <Card
                key={index}
                size="small"
                style={{ marginBottom: 10, borderLeft: "3px solid #722ed1" }}
                bodyStyle={{ padding: "10px 14px" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>
                      {split.title || intl.formatMessage({ id: "document.processing.split.ai.documentFallback" }, { number: index + 1 })}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {split.confidence && (
                      <Tag color={confidenceColor[split.confidence] ?? "default"} style={{ textTransform: "capitalize" }}>
                        {intl.formatMessage({ id: `document.processing.split.ai.confidence.${split.confidence}` })}
                      </Tag>
                    )}
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeEditableSplit(index)}
                    />
                  </div>
                </div>

                {split.detectedKeywords.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    {split.detectedKeywords.map((kw) => (
                      <Tag key={kw} style={{ fontSize: 11 }}>{kw}</Tag>
                    ))}
                  </div>
                )}

                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, color: "#595959", flexShrink: 0, width: 80 }}>
                      {intl.formatMessage({ id: "document.processing.split.pageRanges", defaultMessage: "Pages" })}
                    </span>
                    <Input
                      size="small"
                      value={split.pageInput}
                      placeholder="e.g. 1-3,5,7-9"
                      onChange={(e) => updateSplitPages(index, e.target.value)}
                      style={{ width: 160 }}
                    />
                    {split.pages.length > 0 && (
                      <span style={{ fontSize: 11, color: "#8c8c8c" }}>
                        {intl.formatMessage({ id: "document.processing.split.ai.pageCount" }, { count: split.pages.length })}
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, color: "#595959", flexShrink: 0, width: 80 }}>
                      {intl.formatMessage({ id: "document.processing.split.ai.documentType" })}
                    </span>
                    <Select
                      value={split.documentType || undefined}
                      onChange={(value) => updateSplitType(index, value)}
                      options={documentTypeOptions}
                      size="small"
                      style={{ width: 200 }}
                      placeholder={intl.formatMessage({ id: "document.processing.select.type", defaultMessage: "Select type" })}
                    />
                  </div>
                </div>
              </Card>
            ))}

            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <Button
                icon={<PlusOutlined />}
                onClick={addEditableSplit}
              >
                {intl.formatMessage({ id: "document.processing.split.ai.addPart" })}
              </Button>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleConfirmSplit}
                loading={isConfirming}
                disabled={editableSplits.length === 0}
              >
                {intl.formatMessage({ id: "document.processing.split.ai.confirmButton" })}
              </Button>
            </div>
          </div>
        )}
      </div>

      {!aiOnly && <Divider style={{ margin: "16px 0" }} />}

      {!aiOnly && (
        <>
          {/* Manual Split Section */}
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
              <ScissorOutlined />
              {intl.formatMessage({ id: "document.processing.split.title" })}
            </h3>
            <p style={{ margin: "8px 0 0", color: "#8c8c8c" }}>
              {intl.formatMessage(
                { id: "document.processing.split.splitIntoFiles" },
                { filename: getFilename(), pages: totalPages }
              )}
            </p>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Radio.Group
              value={splitMethod}
              onChange={(e) => setSplitMethod(e.target.value)}
              style={{ marginBottom: 16 }}
            >
              <Radio value="pages">
                {intl.formatMessage({ id: "document.processing.split.byPages" })}
              </Radio>
              <Radio value="ranges">
                {intl.formatMessage({ id: "document.processing.split.byRanges" })}
              </Radio>
            </Radio.Group>

            {splitMethod === "pages" && (
              <div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", marginBottom: 4 }}>
                    {intl.formatMessage({ id: "document.processing.split.pagesPerFile" })}
                  </label>
                  <InputNumber
                    min={1}
                    max={totalPages}
                    value={pagesPerFile}
                    onChange={(value) => setPagesPerFile(value || 1)}
                    style={{ width: 120 }}
                  />
                </div>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={splitByPages}
                  loading={isProcessing}
                  disabled={!pdfBytes}
                >
                  {intl.formatMessage({ id: "document.processing.split.splitAndDownload" })}
                </Button>
              </div>
            )}

            {splitMethod === "ranges" && (
              <div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", marginBottom: 4 }}>
                    {intl.formatMessage({ id: "document.processing.split.pageRanges" })}
                  </label>
                  <Input
                    value={pageRanges}
                    onChange={(e) => setPageRanges(e.target.value)}
                    placeholder="1-3,5,7-9"
                    style={{ width: 200, marginBottom: 8 }}
                  />
                  <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                    <p style={{ margin: "4px 0" }}>
                      {intl.formatMessage({ id: "document.processing.split.examples" })}
                    </p>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li>{intl.formatMessage({ id: "document.processing.split.example1" })}</li>
                      <li>{intl.formatMessage({ id: "document.processing.split.example2" })}</li>
                      <li>{intl.formatMessage({ id: "document.processing.split.example3" })}</li>
                    </ul>
                  </div>
                </div>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={splitByRanges}
                  loading={isProcessing}
                  disabled={!pdfBytes || !pageRanges.trim()}
                >
                  {intl.formatMessage({ id: "document.processing.split.splitAndDownload" })}
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PDFSplitter;
